import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { CountryFlag } from '../../components/CountryFlag';

/**
 * Admin World Cup — single page that drives BOTH WORLD_CUP_PICK6 and
 * WORLD_CUP_SURVIVOR pools. Admin enters results once on the master pool's
 * schedule and the backend cascades scoring/elimination to every linked
 * World Cup pool.
 *
 * Two flows:
 *   1. Knockout match results — pick winner → server cascades scoring.
 *   2. Group-stage advancement — multi-select 32 advancing teams → server
 *      runs Phase 1 gate for Survivor + group-stage points for Pick 6.
 */

interface MasterInfo {
  masterPoolId: string;
  masterPoolName: string;
  masterPoolType: string;
  linkedPools: { id: string; name: string; type: string; hasSchedule: boolean }[];
  /** Teams already marked advanced (derived from entry picks) — used to pre-seed
      the admin's selection so they only need to add new advancements, not re-mark
      everything from scratch each visit. */
  advancedTeamIds?: string[];
}

interface PoolTeam {
  poolId: string;
  teamId: string;
  seed?: number;
  metadata?: { isoCode?: string; fifaRank?: number };
  team: { id: string; name: string; shortName?: string };
}

interface MatchEvent {
  id: string;
  name: string;
  round?: string | null;
  scheduledAt: string;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  winnerId?: string | null;
  status?: string;
  homeTeam?: { id: string; name: string; shortName?: string } | null;
  awayTeam?: { id: string; name: string; shortName?: string } | null;
}

const ROUND_ORDER = [
  'Group Stage - Group A',
  'Group Stage - Group B',
  'Group Stage - Group C',
  'Group Stage - Group D',
  'Group Stage - Group E',
  'Group Stage - Group F',
  'Group Stage - Group G',
  'Group Stage - Group H',
  'Group Stage - Group I',
  'Group Stage - Group J',
  'Group Stage - Group K',
  'Group Stage - Group L',
  'Round of 32',
  'Round of 16',
  'Quarter-Finals',
  'Semi-Finals',
  'Bronze Final',
  'Final',
];

export function AdminWorldCup() {
  const { getAccessTokenSilently } = useAuth0();

  const [master, setMaster] = useState<MasterInfo | null>(null);
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-match draft state: match id → selected winnerId
  const [winnerDraft, setWinnerDraft] = useState<Record<string, string>>({});
  // Per-match submission state
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [matchResult, setMatchResult] = useState<Record<string, string>>({});
  // Per-match override flow — set of match IDs whose admin opened "Override"
  // mode (re-revealing the dropdown for already-decided matches).
  const [overrideOpen, setOverrideOpen] = useState<Set<string>>(new Set());
  // Per-match override confirmation checkbox state — admin must explicitly tick
  // before the Replace button enables, to prevent fat-finger replacements.
  const [overrideConfirmed, setOverrideConfirmed] = useState<Record<string, boolean>>({});
  // Per-match "set teams" flow — for knockout events that don't yet have both
  // teams populated. Set of match IDs that have the inline setter open, plus
  // draft home/away selections per match.
  const [setTeamsOpen, setSetTeamsOpen] = useState<Set<string>>(new Set());
  const [setTeamsDraft, setSetTeamsDraft] = useState<Record<string, { home?: string; away?: string }>>({});
  const [setTeamsSaving, setSetTeamsSaving] = useState<Record<string, boolean>>({});

  // Group stage advancement state
  const [advancingSet, setAdvancingSet] = useState<Set<string>>(new Set());
  const [groupStageProcessing, setGroupStageProcessing] = useState(false);
  const [groupStageMessage, setGroupStageMessage] = useState<string | null>(null);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const masterInfo: MasterInfo = await api.getWorldCupMaster(token);
      setMaster(masterInfo);

      // Pre-seed the selection with teams already marked advanced. Admin can
      // then just ADD newly-advanced teams instead of re-mar­king everything.
      // De-selecting a previously-advanced team here doesn't reverse the cascade
      // — it just changes what gets re-submitted on the next "Save advancements".
      if (Array.isArray(masterInfo.advancedTeamIds)) {
        setAdvancingSet(new Set(masterInfo.advancedTeamIds));
      }

      // Pool fetch returns teams + we need events separately
      const pool = await api.getPool(masterInfo.masterPoolId);
      setTeams((pool.teams as any) || []);

      const evts = await api.getPoolEvents(masterInfo.masterPoolId, token);
      setEvents(evts as any);
    } catch (err: any) {
      console.error('Failed to load World Cup admin', err);
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  // Group events by round
  const eventsByRound = useMemo(() => {
    const grouped: Record<string, MatchEvent[]> = {};
    for (const e of events) {
      const r = e.round || 'Other';
      if (!grouped[r]) grouped[r] = [];
      grouped[r].push(e);
    }
    for (const r of Object.keys(grouped)) {
      grouped[r].sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
    }
    return grouped;
  }, [events]);

  /**
   * Resolve a team ID to its display name + flag ISO code.
   *
   * Preferred source: pool.teams (PoolTeam[]) — has metadata.isoCode for flag rendering.
   * Defensive fallback: the event itself includes homeTeam / awayTeam Prisma relations,
   * which give us at least the team name even when the event references a Team that
   * isn't in the current pool_teams (e.g., orphaned after a team rename + re-import).
   * Without this fallback the UI shows raw UUIDs for orphaned references.
   */
  function getTeamMeta(
    teamId: string | null | undefined,
    eventRelation?: { id: string; name: string; shortName?: string } | null,
  ): { iso?: string; name?: string } {
    if (!teamId) return {};
    const pt = teams.find((t) => t.team.id === teamId);
    if (pt) {
      return { iso: pt.metadata?.isoCode, name: pt.team.name };
    }
    // Orphaned reference — fall back to the event's relation if present
    if (eventRelation && eventRelation.id === teamId) {
      return { name: eventRelation.name };
    }
    return {};
  }

  async function submitMatchResult(eventId: string, isOverride = false) {
    const winner = winnerDraft[eventId];
    if (!winner || !master) return;
    setSubmitting((prev) => ({ ...prev, [eventId]: true }));
    setMatchResult((prev) => ({ ...prev, [eventId]: '' }));
    try {
      const token = await getAccessTokenSilently();
      // Sentinel "DRAW" in the dropdown → send null to the backend, which is
      // how a group-stage draw is recorded (no winnerId, status=COMPLETED).
      const winnerTeamId = winner === 'DRAW' ? null : winner;
      const res = await api.enterWorldCupResult(
        master.masterPoolId,
        { eventId, winnerTeamId, override: isOverride },
        token,
      );
      // Refresh events
      const evts = await api.getPoolEvents(master.masterPoolId, token);
      setEvents(evts as any);
      const cascade = Array.isArray(res.cascade) ? res.cascade : [];
      const reversed = Array.isArray(res.reversed) ? res.reversed : [];
      let summary: string;
      if (winnerTeamId === null) {
        summary = isOverride ? '✓ Draw recorded (replaced previous result)' : '✓ Draw recorded';
      } else if (isOverride && reversed.length > 0) {
        summary = `✓ Result replaced — reversed prior cascade in ${reversed.length} pool${reversed.length !== 1 ? 's' : ''}, then re-cascaded`;
      } else if (cascade.length) {
        summary = `✓ Cascaded to ${cascade.length} pool${cascade.length !== 1 ? 's' : ''}`;
      } else {
        summary = '✓ Result recorded';
      }
      setMatchResult((prev) => ({ ...prev, [eventId]: summary }));
      // Close override mode on success
      if (isOverride) {
        setOverrideOpen((prev) => {
          const next = new Set(prev);
          next.delete(eventId);
          return next;
        });
        setOverrideConfirmed((prev) => ({ ...prev, [eventId]: false }));
      }
    } catch (err: any) {
      setMatchResult((prev) => ({ ...prev, [eventId]: `Error: ${err?.message || 'failed'}` }));
    } finally {
      setSubmitting((prev) => ({ ...prev, [eventId]: false }));
    }
  }

  /**
   * Save the home + away team selections for a knockout event whose teams were
   * left TBD at schedule-import time. Backed by PUT /admin/events/:id/teams
   * (the same endpoint AdminResults' SetTeamsModal uses). After save, refresh
   * the events list so the new teams + result dropdown become available.
   */
  async function saveTeamsForMatch(eventId: string) {
    const draft = setTeamsDraft[eventId];
    if (!master || !draft?.home || !draft?.away) return;
    if (draft.home === draft.away) {
      setMatchResult((prev) => ({ ...prev, [eventId]: 'Error: home and away must be different teams' }));
      return;
    }
    setSetTeamsSaving((prev) => ({ ...prev, [eventId]: true }));
    setMatchResult((prev) => ({ ...prev, [eventId]: '' }));
    try {
      const token = await getAccessTokenSilently();
      await api.updateEventTeams(eventId, { homeTeamId: draft.home, awayTeamId: draft.away }, token);
      const evts = await api.getPoolEvents(master.masterPoolId, token);
      setEvents(evts as any);
      setSetTeamsOpen((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
      setMatchResult((prev) => ({ ...prev, [eventId]: '✓ Teams saved — you can now enter a result' }));
    } catch (err: any) {
      setMatchResult((prev) => ({ ...prev, [eventId]: `Error: ${err?.message || 'failed'}` }));
    } finally {
      setSetTeamsSaving((prev) => ({ ...prev, [eventId]: false }));
    }
  }

  function toggleAdvancing(teamId: string) {
    setAdvancingSet((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }

  async function submitGroupAdvancement(mode: 'incremental' | 'finalize') {
    if (advancingSet.size === 0 || !master) return;
    if (mode === 'finalize' && advancingSet.size !== 32) {
      setGroupStageMessage('Error: Finalize requires exactly 32 teams selected');
      return;
    }
    if (mode === 'finalize') {
      const ok = window.confirm(
        'Finalize group stage?\n\nThis will mark all teams NOT in your selection as eliminated, locking in the final 32-team knockout field. Any Survivor entry whose pick is eliminated will be knocked out. Cannot be easily undone.',
      );
      if (!ok) return;
    }
    setGroupStageProcessing(true);
    setGroupStageMessage(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.advanceWorldCupGroupStage(
        master.masterPoolId,
        { advancingTeamIds: Array.from(advancingSet), mode },
        token,
      );
      const cascade = Array.isArray(res.cascade) ? res.cascade : [];
      setGroupStageMessage(
        mode === 'finalize'
          ? `✓ Group stage finalized — cascaded to ${cascade.length} pool${cascade.length !== 1 ? 's' : ''}`
          : `✓ Saved advancements (${advancingSet.size} team${advancingSet.size !== 1 ? 's' : ''}) — cascaded to ${cascade.length} pool${cascade.length !== 1 ? 's' : ''}`,
      );
    } catch (err: any) {
      setGroupStageMessage(`Error: ${err?.message || 'failed'}`);
    } finally {
      setGroupStageProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-semibold">Failed to load</p>
          <p className="text-sm mt-1">{error}</p>
          <Link to="/admin" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
            ← Back to Admin
          </Link>
        </div>
      </div>
    );
  }

  if (!master) return null;

  const orderedRounds = ROUND_ORDER.filter((r) => eventsByRound[r]);
  const allGroupComplete = (eventsByRound['Group Stage - Group A'] || []).every((e) => !!e.winnerId)
    && (eventsByRound['Group Stage - Group L'] || []).every((e) => !!e.winnerId);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <Link to="/admin" className="text-blue-600 hover:underline text-sm">← Back to Admin</Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">World Cup — Master Schedule</h1>
        <p className="text-gray-600 mt-1">
          Enter each match result once. Scoring cascades automatically to every linked World Cup pool below.
        </p>
      </div>

      {/* Master + linked pools card */}
      <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h2 className="font-semibold text-emerald-900 mb-2">⚽ Master pool: {master.masterPoolName}</h2>
        <p className="text-sm text-emerald-800 mb-3">
          Cascades to <strong>{master.linkedPools.length}</strong> linked pool{master.linkedPools.length !== 1 ? 's' : ''}:
        </p>
        <div className="flex flex-wrap gap-2">
          {master.linkedPools.map((p) => (
            <span
              key={p.id}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                p.id === master.masterPoolId ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-800 border border-emerald-300'
              }`}
            >
              {p.name} <span className="opacity-70">({p.type === 'WORLD_CUP_PICK6' ? 'Pick 6' : 'Survivor'})</span>
            </span>
          ))}
        </div>
      </div>

      {/* Group stage advancement panel */}
      <div className="mb-8 bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="font-semibold text-gray-900 mb-2">Process Group Stage Advancement</h2>
        <p className="text-sm text-gray-600 mb-3">
          Mark teams as they advance — you don't have to wait for all 72 group matches to finish.
          Use <strong>Save advancements</strong> incrementally as groups resolve; Survivor entries
          unlock Phase 2 picks as soon as all six of their group picks are marked advanced.
          When the full 32-team field is known (top 2 per group + 8 best 3rd-place teams),
          click <strong>Finalize group stage</strong> to eliminate any team not in your selection.
        </p>
        <p className="text-xs text-gray-500 mb-3 italic">
          Teams you've previously advanced are pre-selected below. Just add newly-advanced teams
          on top — the cascade is idempotent (resubmitting an already-advanced team is a no-op).
        </p>
        <p className={`text-sm font-medium mb-3 ${advancingSet.size === 32 ? 'text-emerald-700' : advancingSet.size > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
          Selected: {advancingSet.size}
          {advancingSet.size < 32 && <span className="text-gray-500"> / 32 (need 32 to finalize)</span>}
          {!allGroupComplete && <span className="text-amber-600 ml-2">(group matches not all complete yet)</span>}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4 max-h-72 overflow-y-auto pr-1">
          {[...teams].sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99)).map((t) => {
            const checked = advancingSet.has(t.team.id);
            return (
              <button
                key={t.team.id}
                type="button"
                onClick={() => toggleAdvancing(t.team.id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded border text-left text-sm transition-colors ${
                  checked
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-900'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t.metadata?.isoCode && <CountryFlag iso={t.metadata.isoCode} alt={t.team.name} size="xs" rounded />}
                <span className="truncate">Seed {t.seed} · {t.team.shortName || t.team.name}</span>
                {checked && <span className="ml-auto text-emerald-600">✓</span>}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => submitGroupAdvancement('incremental')}
            disabled={advancingSet.size === 0 || groupStageProcessing}
            title="Additively mark the selected teams as advanced. Teams not selected stay PENDING. Use as each group finishes."
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {groupStageProcessing ? 'Processing…' : `Save advancements (${advancingSet.size})`}
          </button>
          <button
            type="button"
            onClick={() => submitGroupAdvancement('finalize')}
            disabled={advancingSet.size !== 32 || groupStageProcessing}
            title="Lock in the final 32-team field. Any team not in your selection is marked eliminated. Use after all 72 group matches finish and the 8 best 3rd-place teams are decided."
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {groupStageProcessing ? 'Processing…' : 'Finalize group stage'}
          </button>
          {advancingSet.size > 0 && (
            <button
              type="button"
              onClick={() => setAdvancingSet(new Set())}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear selection
            </button>
          )}
          {groupStageMessage && (
            <span className={`text-sm font-medium ${groupStageMessage.startsWith('✓') ? 'text-emerald-700' : 'text-rose-600'}`}>
              {groupStageMessage}
            </span>
          )}
        </div>
      </div>

      {/* Schedule, grouped by round */}
      <div className="space-y-6">
        {orderedRounds.map((round) => {
          const matches = eventsByRound[round] || [];
          // "Complete" = match has either a winner OR was recorded as a draw
          // (group-stage draws have status=COMPLETED but no winnerId).
          const completed = matches.filter(
            (m) => !!m.winnerId || m.status === 'COMPLETED',
          ).length;
          // Group-stage matches can end in a draw. Knockout rounds always have
          // a winner (extra time / penalties decide).
          const isGroupStage = round.startsWith('Group Stage');
          return (
            <div key={round} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{round}</h3>
                <span className="text-xs text-gray-500">{completed} / {matches.length} complete</span>
              </div>
              <div className="divide-y divide-gray-100">
                {matches.map((match) => {
                  const home = getTeamMeta(match.homeTeamId, match.homeTeam);
                  const away = getTeamMeta(match.awayTeamId, match.awayTeam);
                  const isDraw = !match.winnerId && match.status === 'COMPLETED';
                  const decided = !!match.winnerId || isDraw;
                  const teamsKnown = !!match.homeTeamId && !!match.awayTeamId;
                  const draft = winnerDraft[match.id];
                  const isSubmitting = !!submitting[match.id];
                  const result = matchResult[match.id];
                  const dateStr = new Date(match.scheduledAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  });

                  return (
                    <div key={match.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                      <div className="text-xs text-gray-500 w-32 flex-shrink-0">{dateStr}</div>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Home */}
                        <div className={`flex items-center gap-2 ${match.winnerId === match.homeTeamId ? 'font-semibold text-emerald-700' : ''}`}>
                          {home.iso && <CountryFlag iso={home.iso} alt={home.name} size="sm" rounded />}
                          <span className="truncate">{home.name || match.homeTeamId || <span className="italic text-gray-400">TBD</span>}</span>
                        </div>
                        <span className="text-gray-400 text-sm">vs</span>
                        {/* Away */}
                        <div className={`flex items-center gap-2 ${match.winnerId === match.awayTeamId ? 'font-semibold text-emerald-700' : ''}`}>
                          {away.iso && <CountryFlag iso={away.iso} alt={away.name} size="sm" rounded />}
                          <span className="truncate">{away.name || match.awayTeamId || <span className="italic text-gray-400">TBD</span>}</span>
                        </div>
                      </div>
                      {/* Action area */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {decided && !overrideOpen.has(match.id) ? (
                          <>
                            {isDraw ? (
                              <span className="text-sm font-medium text-amber-700">⚖️ Draw</span>
                            ) : (
                              <span className="text-sm font-medium text-emerald-700">
                                ✓ Winner: {match.winnerId === match.homeTeamId ? home.name : away.name}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setOverrideOpen((prev) => {
                                  const next = new Set(prev);
                                  next.add(match.id);
                                  return next;
                                });
                                // Prefill draft with current winner so admin only changes if needed
                                setWinnerDraft((prev) => ({
                                  ...prev,
                                  [match.id]: match.winnerId || 'DRAW',
                                }));
                              }}
                              className="text-xs text-gray-500 hover:text-amber-700 underline"
                              title="Replace the previously-entered result. This will reverse the prior cascade (remove awarded points, revive eliminated entries) and apply the new result."
                            >
                              Override
                            </button>
                          </>
                        ) : decided && overrideOpen.has(match.id) ? (
                          <>
                            <select
                              value={draft || ''}
                              onChange={(e) => setWinnerDraft((p) => ({ ...p, [match.id]: e.target.value }))}
                              className="text-sm border-2 border-amber-400 rounded px-2 py-1 bg-amber-50"
                              disabled={isSubmitting}
                            >
                              <option value="">Pick corrected result…</option>
                              <option value={match.homeTeamId!}>{home.name} wins</option>
                              <option value={match.awayTeamId!}>{away.name} wins</option>
                              {isGroupStage && <option value="DRAW">Draw</option>}
                            </select>
                            <label className="flex items-center gap-1 text-xs text-amber-800">
                              <input
                                type="checkbox"
                                checked={!!overrideConfirmed[match.id]}
                                onChange={(e) =>
                                  setOverrideConfirmed((p) => ({ ...p, [match.id]: e.target.checked }))
                                }
                                disabled={isSubmitting}
                                className="w-3.5 h-3.5"
                              />
                              I'm replacing the prior result
                            </label>
                            <button
                              type="button"
                              onClick={() => submitMatchResult(match.id, true)}
                              disabled={!draft || !overrideConfirmed[match.id] || isSubmitting}
                              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmitting ? 'Replacing…' : 'Replace result'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setOverrideOpen((prev) => {
                                  const next = new Set(prev);
                                  next.delete(match.id);
                                  return next;
                                });
                                setOverrideConfirmed((prev) => ({ ...prev, [match.id]: false }));
                              }}
                              className="text-xs text-gray-500 hover:text-gray-800 underline"
                              disabled={isSubmitting}
                            >
                              Cancel
                            </button>
                          </>
                        ) : teamsKnown ? (
                          <>
                            <select
                              value={draft || ''}
                              onChange={(e) => setWinnerDraft((p) => ({ ...p, [match.id]: e.target.value }))}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                              disabled={isSubmitting}
                            >
                              <option value="">Pick result…</option>
                              <option value={match.homeTeamId!}>{home.name} wins</option>
                              <option value={match.awayTeamId!}>{away.name} wins</option>
                              {isGroupStage && <option value="DRAW">Draw</option>}
                            </select>
                            <button
                              type="button"
                              onClick={() => submitMatchResult(match.id)}
                              disabled={!draft || isSubmitting}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmitting ? 'Saving…' : 'Submit'}
                            </button>
                          </>
                        ) : setTeamsOpen.has(match.id) ? (
                          // Inline "Set teams" form — pick home + away from pool teams
                          (() => {
                            const draft = setTeamsDraft[match.id] || {};
                            const isSaving = !!setTeamsSaving[match.id];
                            const sortedTeams = [...teams].sort((a, b) =>
                              (a.seed ?? 99) - (b.seed ?? 99) ||
                              a.team.name.localeCompare(b.team.name),
                            );
                            return (
                              <>
                                <select
                                  value={draft.home || ''}
                                  onChange={(e) =>
                                    setSetTeamsDraft((p) => ({
                                      ...p,
                                      [match.id]: { ...p[match.id], home: e.target.value },
                                    }))
                                  }
                                  disabled={isSaving}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 max-w-[10rem]"
                                >
                                  <option value="">Home…</option>
                                  {sortedTeams.map((t) => (
                                    <option key={t.team.id} value={t.team.id}>
                                      Seed {t.seed} · {t.team.name}
                                    </option>
                                  ))}
                                </select>
                                <span className="text-gray-400 text-xs">vs</span>
                                <select
                                  value={draft.away || ''}
                                  onChange={(e) =>
                                    setSetTeamsDraft((p) => ({
                                      ...p,
                                      [match.id]: { ...p[match.id], away: e.target.value },
                                    }))
                                  }
                                  disabled={isSaving}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 max-w-[10rem]"
                                >
                                  <option value="">Away…</option>
                                  {sortedTeams.map((t) => (
                                    <option key={t.team.id} value={t.team.id}>
                                      Seed {t.seed} · {t.team.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => saveTeamsForMatch(match.id)}
                                  disabled={!draft.home || !draft.away || isSaving}
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isSaving ? 'Saving…' : 'Save teams'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSetTeamsOpen((prev) => {
                                      const next = new Set(prev);
                                      next.delete(match.id);
                                      return next;
                                    });
                                  }}
                                  disabled={isSaving}
                                  className="text-xs text-gray-500 hover:text-gray-800 underline"
                                >
                                  Cancel
                                </button>
                              </>
                            );
                          })()
                        ) : (
                          <>
                            <span className="text-xs italic text-gray-400">Teams TBD</span>
                            <button
                              type="button"
                              onClick={() => {
                                setSetTeamsOpen((prev) => {
                                  const next = new Set(prev);
                                  next.add(match.id);
                                  return next;
                                });
                                // Prefill with whatever's already set (one team set, other not)
                                setSetTeamsDraft((prev) => ({
                                  ...prev,
                                  [match.id]: {
                                    home: match.homeTeamId || undefined,
                                    away: match.awayTeamId || undefined,
                                  },
                                }));
                              }}
                              className="px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded"
                              title="Manually set the home and away teams for this match (e.g. once you know who won their group)."
                            >
                              Set teams
                            </button>
                          </>
                        )}
                        {result && (
                          <span className={`text-xs ${result.startsWith('✓') ? 'text-emerald-700' : 'text-rose-600'}`}>
                            {result}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AdminWorldCup;
